import axios from 'axios';


const api = axios.create({
  baseURL: window.location.host.includes("localhost") ? 'http://localhost:8080' : window.location.origin + '/rys-ws'
});

const MAX_TRANSIENT_RETRIES = 6;
const RETRY_BASE_DELAY_MS = 1000;

function shouldRetryRequest(error) {
  const status = error?.response?.status;
  const method = (error?.config?.method || 'get').toLowerCase();
  const idempotentMethod = ['get', 'head', 'options'].includes(method);
  const transientStatus = status === 502 || status === 503 || status === 504;
  const networkError = !error?.response;
  return idempotentMethod && (networkError || transientStatus);
}

function getRetryDelay(attempt) {
  return Math.min(RETRY_BASE_DELAY_MS * (2 ** (attempt - 1)), 8000);
}

// Add a request interceptor to include JWT if present
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers = config.headers || {};
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Global response handler: if token is invalid/expired, clear and redirect to login
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error?.config || {};
    config.__retryCount = config.__retryCount || 0;

    if (shouldRetryRequest(error) && config.__retryCount < MAX_TRANSIENT_RETRIES) {
      config.__retryCount += 1;
      const delay = getRetryDelay(config.__retryCount);
      await new Promise((resolve) => setTimeout(resolve, delay));
      return api(config);
    }

    const status = error?.response?.status;
    const url = error?.config?.url || '';
    // If this was a login attempt, let the Login page handle 401 UI instead of forcing a redirect.
    if (status === 401 && url && url.includes('/api/auth/login')) {
      return Promise.reject(error);
    }
    if (status === 401) {
      localStorage.removeItem('token');
      // redirect to app login
      try { window.location.href = '/login'; } catch (e) { /* ignore */ }
    }
    return Promise.reject(error);
  }
);

export default api;

export async function downloadProjectKickoffCalendar(sessionKey) {
  const response = await api.get(`/api/public/kickoff-sessions/${sessionKey}/calendar`, {
    responseType: 'blob',
    validateStatus: () => true,
  });
  const contentType = String(response.headers?.['content-type'] || '').toLowerCase();
  if (response.status < 200 || response.status >= 300 || !contentType.startsWith('text/calendar')) {
    throw new Error('The calendar file is unavailable. Please try again later.');
  }
  return {
    blob: response.data,
    contentDisposition: response.headers?.['content-disposition'] || '',
  };
}
